import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Textarea from '@/components/forms/Textarea';
import { descriptionRegex, nameValidationRegex } from '@/constants/regex';
import {
  useCreatePointCategory,
  useUpdatePointCategory,
} from '@/features/points-management/points-configuration/hooks/usePointCategoryMutations';
import {
  getEncryptedPointAgentType,
  PointAgentTab,
} from '@/features/points-management/points-configuration/pointConfiguration.constants';
import { useDynamicLocale } from '@/hoslog-intl/Translation';
import { PointCategoryDetails } from '@/services/pointConfiguration/pointConfiguration.types';
import { z } from 'zod';

const schema = z.object({
  categoryName: z
    .string()
    .min(1, { message: 'common.form.validation.required' })
    .refine((val) => val == null || val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    }),
  description: z
    .string()
    .min(1, { message: 'common.form.validation.required' })
    .refine((val) => val == null || val === '' || descriptionRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    }),
});

const ManagePointCategory = ({
  activeTab,
  category,
}: {
  activeTab: PointAgentTab;
  category?: PointCategoryDetails;
}) => {
  const form = useForm({
    schema,
    mode: 'onBlur',
    defaultValues: { categoryName: category?.categoryName ?? '', description: category?.description ?? '' },
  });
  const { mutateAsync: createCategory } = useCreatePointCategory();
  const { mutateAsync: updateCategory } = useUpdatePointCategory();
  const agentType = getEncryptedPointAgentType(activeTab);
  const { t } = useDynamicLocale();

  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        if (category) {
          await updateCategory({
            pointCategoryId: category.pointCategoryId,
            agentType,
            ...data,
          });
        } else await createCategory({ agentType, ...data });
      }}
    >
      <div className="w-full flex flex-col gap-4.5">
        <Input
          name="userType"
          maxLength={50}
          label="pointManagement.page.form.managePointCategory.userType"
          value={activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          disabled
        />
        <Input
          label="pointManagement.page.form.managePointCategory.categoryName"
          maxLength={50}
          placeholder={t('configurationManagement.commissions.manageCommissionModal.placeholder.enterCategoryName')}
          {...form.register('categoryName')}
        />
        <Textarea
          label="pointManagement.page.form.managePointCategory.description"
          maxLength={200}
          placeholder={t('configurationManagement.commissions.manageCommissionModal.placeholder.writeSomething')}
          {...form.register('description')}
        />
      </div>
    </Form>
  );
};

export default ManagePointCategory;
